import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useAuth } from '@/contexts/AuthContext'
import api from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Building2, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'

const OrgClinics: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>()
  const { orgRole, exitOrgView } = useOrganization()
  const { selectClinic } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [openingClinicId, setOpeningClinicId] = React.useState<string | null>(null)

  const { data: clinicsData, isLoading } = useQuery({
    queryKey: ['org-clinics', orgId],
    queryFn: async () => {
      const res = await api.get(`/organizations/${orgId}/clinics`)
      return res.data?.data
    },
    enabled: !!orgId,
  })

  const clinics = clinicsData?.clinics || []

  const handleDrillDown = async (clinicId: string) => {
    try {
      setOpeningClinicId(clinicId)
      // Explicitly select clinic server-side so user context is updated
      await selectClinic(clinicId)
      localStorage.setItem('selectedClinicId', clinicId)
      localStorage.setItem('selectedClinicTimestamp', Date.now().toString())
      exitOrgView()
      navigate('/', { replace: true })
    } catch (error) {
      toast({
        title: 'Unable to open clinic',
        description: 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setOpeningClinicId(null)
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clinics</h1>
          <p className="text-sm text-slate-500 mt-1">
            {clinics.length} clinic{clinics.length !== 1 ? 's' : ''} in your organization
          </p>
        </div>
        {(orgRole === 'org_owner' || orgRole === 'org_admin') && (
          <Button variant="outline" disabled>
            Add Clinic (coming soon)
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-40 w-full rounded-lg" />
          ))}
        </div>
      ) : clinics.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No clinics yet</h3>
            <p className="text-sm text-slate-500 mt-1">
              Clinics will appear here once they are associated with this organization
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clinics.map((clinic: Record<string, unknown>) => (
            <Card key={clinic.id as string} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-base">{clinic.name as string}</CardTitle>
                  <Badge variant={(clinic.status as string) === 'active' ? 'default' : 'secondary'}>
                    {clinic.status as string}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-slate-500">
                  {(clinic.city || clinic.state) && (
                    <p>
                      {clinic.city as string}{clinic.state ? `, ${clinic.state}` : ''}
                    </p>
                  )}
                  {clinic.slug && (
                    <p className="text-xs text-slate-400">Slug: {clinic.slug as string}</p>
                  )}
                </div>
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDrillDown(clinic.id as string)}
                    className="flex-1"
                    disabled={openingClinicId === (clinic.id as string)}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    {openingClinicId === (clinic.id as string) ? 'Opening...' : 'Open Clinic'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default OrgClinics
